{
  "sharepoint": {
    "title": "SharePoint Integration",
    "description": "Connect your SharePoint tenant to automatically mirror published documents. This wizard will guide you through the setup process.",
    "steps": {
      "adminConsent": {
        "title": "Admin Consent",
        "description": "Grant permissions to access SharePoint",
        "header": "Admin Consent & Permissions",
        "subtitle": "Grant the necessary permissions for SharePoint integration",
        "adminRequiredDesc": "You must be a Privileged Role Administrator or Global Administrator to grant admin consent for this application.",
        "requiredPermissions": "We will request the following permissions:",
        "application": "Application Permissions",
        "applicationDesc": "Application permissions lets North House act without a signed in user. This is the permission we use after the setup process is complete.",
        "delegated": "Delegated Permissions",
        "delegatedDesc": "Delegated permissions let us act on behalf of the user. We use these permission during the setup process to make the onboarding process easier.",
        "sitesSelected": "Sites.Selected",
        "sitesSelectedDesc": "Access specific SharePoint sites after permission is granted",
        "userRead": "User.Read",
        "userReadDesc": "Allows users to sign-in to the app. This permission is always requested.",
        "whatHappensNext": "What happens next:",
        "redirectToConsent": "You'll be redirected to Microsoft's consent page",
        "reviewPermissions": "Review and approve the requested permissions",
        "returnToContinue": "Return to continue the setup process",
        "grantConsent": "Grant Admin Consent",
        "redirecting": "Redirecting..."
      },
      "signIn": {
        "title": "Sign In",
        "description": "Authenticate with Microsoft",
        "header": "Sign In with Microsoft",
        "subtitle": "Authenticate with your Microsoft account to access SharePoint",
        "authDetails": "Authentication Details:",
        "sessionUsage": "Your authentication session will be used to list and select SharePoint sites",
        "tenantStorage": "We'll store tenant information like siteId and driveId to configure the integration",
        "revokeAccess": "You can revoke access from your Microsoft account settings when this integration is complete",
        "tenantId": "Tenant ID:",
        "signInButton": "Sign In with Microsoft"
      },
      "selectDestination": {
        "title": "Select Destination",
        "description": "Choose site or library for integration",
        "header": "Select Destination",
        "subtitle": "Choose the SharePoint site or document library where documents will be mirrored",
        "site": "Site",
        "drive": "Document Library",
        "error": "Failed to select destination",
        "searchTab": "Search Site",
        "manualTab": "Enter Site URL",
        "urlPlaceholder": "https://your-tenant.sharepoint.com/sites/site-name",
        "urlHint": "Paste the URL of your SharePoint site. You can copy this from your browser's address bar when viewing the site.",
        "urlInvalid": "Invalid SharePoint site URL or site not accessible. Please check the URL and ensure you have access to the site.",
        "siteFound": "Site found",
        "noSitesFound": "No sites found",
        "selectSiteFirst": "Select a site first"
      },
      "verifyAccess": {
        "title": "Verify Access",
        "description": "Verify access to the selected SharePoint site or library",
        "header": "Verify Access",
        "subtitle": "We will verify access to the selected SharePoint site or library med application token",
        "success": "Access verified successfully",
        "error": "Failed to verify access",
        "errorDescription": "Please try to reload the page, otherwise contact support at support@northhouse.se"
      },
      "grantAccess": {
        "title": "Grant App Access",
        "description": "Grant us access to the selected scope",
        "header": "Grant App Access",
        "subtitle": "Grant application access to the selected SharePoint site or library",
        "info": "We'll request Microsoft Graph to add an application permission with role 'write' for the selected scope.",
        "error": "Failed to grant application access"
      }
    },
    "completion": {
      "header": "Setup Complete",
      "subtitle": "Your SharePoint integration is configured and ready",
      "integrationActive": "Integration Active"
    }
  },
  "msIntegration": {
    "tokenNotFound": "The URL is invalid. Please request a new one and try again.",
    "title": "North House Microsoft Integration",
    "subtitle": "Complete the steps below to set up the Microsoft integration for your organization.",
    "sidebar": {
      "adminConsent": "Admin Consent",
      "adminConsentDesc": "Grant permissions to North House",
      "userMigration": "Transfer Assignments",
      "userMigrationDesc": "Transfer user assignments to the new app",
      "speSetup": "Document Storage",
      "speSetupDesc": "Set up SharePoint Embedded and migrate documents"
    },
    "steps": {
      "adminConsent": {
        "title": "Grant Admin Consent",
        "description": "Authorize North House to access your Microsoft tenant"
      },
      "userMigration": {
        "title": "Migrate User Assignments",
        "description": "Transfer existing user assignments to the new application",
        "skipButton": "Skip this step"
      }
    },
    "permissionsRequired": "Required Permissions",
    "permissionsDescription": "You will be asked to grant the following API permissions in your tenant:",
    "ssoPermissions": "SSO Authentication",
    "ssoPermissionsDesc": "email, offline_access, openid, profile - Required for secure single sign-on (SSO) using OpenID Connect",
    "userReadPermission": "User.Read",
    "userReadPermissionDesc": "Always required for Microsoft Graph integrations to function properly",
    "fileStoragePermission": "FileStorageContainer.Selected",
    "fileStoragePermissionDesc": "Allows North House to create, access and manage isolated containers in your tenant for document storage and synchronization. Used for SharePoint Embedded integration.",
    "sitesSelectedPermission": "Sites.Selected",
    "sitesSelectedPermissionDesc": "Allows North House to mirror published documents to a specific site in your tenant. This feature is optional. Granting admin consent will not provide access; permission for a site must be explicitly granted.",
    "containerTypeRegPermission": "FileStorageContainerTypeReg.Selected",
    "containerTypeRegPermissionDesc": "Allows North House to register container types in your tenant for SharePoint Embedded. Required to create storage containers for your organization's documents.",
    "sensitivityLabelsPermission": "SensitivityLabels.Read.All",
    "sensitivityLabelsPermissionDesc": "Allows North House to read sensitivity labels configured in your tenant. Used to display available sensitivity labels and apply them to documents.",
    "signIn": {
      "title": "Administrator Sign-In",
      "button": "Sign In with Microsoft",
      "signingIn": "Signing in...",
      "error": "Failed to initiate sign-in. Please try again.",
      "description": "To migrate user assignments from your previous application, we need you to sign in with an administrator account. This allows us to read the existing assignments and recreate them on the new North House application.",
      "skipUserMigration": "Skip and do it manually in Entra"
    },
    "adminCheck": {
      "button": "Give Admin Consent",
      "isAdmin": "Admin role verified",
      "description": "Set up North House Microsoft integration to enable secure single sign-on (SSO) and SharePoint integration for your organization.",
      "details": "This process will register North House as a service principal in your Microsoft Entra tenant.",
      "privacyNote": "North House only accesses the specific resources granted and can't access data outside the approved scope."
    },
    "error": {
      "title": "Setup Failed",
      "description": "An error occurred during the integration setup. Our team has been notified. Please verify your SharePoint URL is correct and try again. If the problem persists, contact support at support@northhouse.se or call +46 (0)8 50 23 52 22."
    },
    "status": {
      "creatingContainer": "Creating SharePoint Storage Container",
      "waitingForMigration": "Documents are scheduled for migration tonight.",
      "migrating": "Migrating Documents",
      "completed": "All documents have been successfully migrated",
      "error": "Error",
      "migratingDescription": "Migrating documents to SharePoint Embedded. You can safely close this window.",
      "failed": "failed",
      "startNowTitle": "North House will be unavailable",
      "startNowButton": "Start Now",
      "startNowWarning": "The application will be unavailable during the migration. Only start now if no users are currently working in the system.",
      "userAssignment": {
        "loadingAssignments": "Loading existing assignments...",
        "loadError": "Failed to load assignments. The sign-in session may have expired.",
        "noAssignments": "No user or group assignments found on the previous application. No migration needed.",
        "assignmentsFound_one": "Found {{count}} assignment to migrate:",
        "assignmentsFound_other": "Found {{count}} assignments to migrate:",
        "migrateButton_one": "Migrate {{count}} assignment",
        "migrateButton_other": "Migrate {{count}} assignments",
        "migrating": "Migrating...",
        "migrateSuccess_one": "Successfully migrated {{count}} assignment.",
        "migrateSuccess_other": "Successfully migrated {{count}} assignments.",
        "migratePartial": "Migrated {{migrated}} assignments, but {{failed}} failed. You may need to assign those manually.",
        "migrateError": "Failed to migrate assignments. Please try again or assign users manually.",
        "user": "User",
        "group": "Group"
      }
    }
  }
}
